import { CENSOR, SENSITIVE_KEYS } from './sensitive-keys.js';
const fieldName = (key) => key.replace(/[^a-z0-9]/gi, '').toLowerCase();
const CREDENTIALS = new Set([...SENSITIVE_KEYS].map(fieldName));
CREDENTIALS.add('setcookie');
CREDENTIALS.add('cookies');
CREDENTIALS.add('xapikey');
const CLINIC = /^(?:clinic|organization|organisation|workspace)$/;
const PERSON = /^(?:patient|customer|contact|lead|user|client)(?:profile|details|info|data)?$/;
const PRIVATE_PERSON_FIELD = /^(?:name|firstname|lastname|fullname|displayname|username|email|emailaddress|phone|phonenumber|mobile|mobilenumber|telephone|address|addressline[12]|postcode|zipcode|dob|dateofbirth|birthdate|medicalhistory|healthinformation|medicalinformation|treatmenthistory|notes?|diagnosis|symptoms?|message|messages|body|text|transcript)$/;
const PRIVATE_FIELD = /^(?:firstname|lastname|fullname|email|emailaddress|phone|phonenumber|mobile|mobilenumber|telephone|dateofbirth|birthdate|dob|ipaddress|clientip|clinicalnotes?|medicalnotes?|healthnotes?|appointmentnotes?|healthinformation|medicalinformation|medicalhistory|treatmenthistory|diagnosis|symptoms?|messagepreview|messagetext|inboundpreview|chattranscript|transcript)$/;
const CLINIC_NAME = /^(?:clinic|organization|organisation|workspace)(?:name|displayname|legalname|tradingname)$/;
const idField = (field) => /(?:id|ids|uuid|uuids)$/.test(field) || field === 'groupkey';
function isPrivate(field, parent) {
    const personField = field.replace(/^(?:patient|customer|contact|lead|user|client)/, '');
    return (CLINIC_NAME.test(field) ||
        PRIVATE_FIELD.test(field) ||
        (/^(?:query|querystring)$/.test(parent) &&
            /^(?:name|address|notes)$/.test(field)) ||
        (field === 'ip' && /^(?:context|request|user)$/.test(parent)) ||
        (personField !== field && PRIVATE_PERSON_FIELD.test(personField)) ||
        (PERSON.test(parent) && PRIVATE_PERSON_FIELD.test(field)) ||
        (CLINIC.test(parent) &&
            /^(?:name|displayname|legalname|tradingname)$/.test(field)));
}
const CONTACT_PARAMETER = /((?:^|[?&])(?:name|first_?name|last_?name|full_?name|email|phone|mobile|clinic_?name|organi[sz]ation_?name|patient_?name|date_?of_?birth|dob|address|notes|symptoms|(?:access_|refresh_)?token|signature|x-amz-(?:signature|credential|security-token))=)[^&\s"'#<>]+/gi;
const LABELLED_JSON_VALUE = /"([\w.-]+)"(\s*:\s*)"((?:\\.|[^"\\])*)"/g;
const EMAIL = /\b[\w.+-]+(?:@|%40)[\w.-]+\.[a-z]{2,}\b/gi;
const INTERNATIONAL_PHONE = /(?<![\w-])\+(?:\d[ ().-]*){7,14}\d(?![\w-])/g;
const LABELLED_PHONE = /\b((?:phone(?:Number)?|mobile(?:Number)?|tel)\s*[:=]\s*)\+?(?:\d[ ().-]*){7,14}\d(?![\w-])/gi;
const SQL_PARAMETERS = /(^[ \t]*params:[ \t]*|\bFailed query:[^\n]*?\bparams:[ \t]*)[^\n]*(?:\n(?![ \t]*at )[^\n]*)*/gim;
const SQL_ROW = /(\bFailing row contains \()[^\n]*(\)\.?)/gi;
const SQL_LITERAL = /'(?:''|[^'])*'/g;
const DATABASE_KEY_VALUE = /(\bKey \([^\n)]*\)=\()[^\n]*(\) (?:already exists|is not present)[^\n]*)/gi;
const CREDENTIAL_TEXT = /\b(Bearer[ \t]+)[A-Za-z0-9._~+/-]+=*/gi;
const TOKEN_PATH = /(\/(?:reset-password|verify-email|magic-link|impersonate|mobile-verify|mobile-complete|invitations\/token|book\/manage)\/)[^/?#\s"'<>]+/gi;
const SETUP_TOKEN_PATH = /((?:^|[ \t("'=]|https?:\/\/[^/\s]+)\/start\/)[^/?#\s"'<>]+/gi;
const URL_CREDENTIALS = /([a-z][a-z\d+.-]*:\/\/)[^/\s@]+:[^/\s@]+@/gi;
function redactText(text, knownValues) {
    const safe = text
        .replace(LABELLED_JSON_VALUE, (match, key, separator) => isPrivate(fieldName(key), '') || CREDENTIALS.has(fieldName(key))
        ? `"${key}"${separator}"${CENSOR}"`
        : match)
        .replace(CONTACT_PARAMETER, `$1${CENSOR}`)
        .replace(URL_CREDENTIALS, '$1')
        .replace(EMAIL, '[EMAIL]')
        .replace(INTERNATIONAL_PHONE, '[PHONE]')
        .replace(LABELLED_PHONE, '$1[PHONE]')
        .replace(SQL_PARAMETERS, `$1${CENSOR}`)
        .replace(DATABASE_KEY_VALUE, `$1${CENSOR}$2`)
        .replace(SQL_ROW, `$1${CENSOR}$2`)
        .replace(CREDENTIAL_TEXT, `$1${CENSOR}`)
        .replace(TOKEN_PATH, `$1${CENSOR}`)
        .replace(SETUP_TOKEN_PATH, `$1${CENSOR}`);
    return knownValues ? safe.replace(knownValues, CENSOR) : safe;
}
function entries(value) {
    if (value instanceof Error) {
        return Object.entries({
            ...value,
            name: value.name,
            message: value.message,
            stack: value.stack,
            cause: value.cause,
        });
    }
    return Object.entries(value);
}
const MAX_DEPTH = 32;
function collectPrivateValues(value, knownValues, parent = '', depth = 0, seen = new WeakSet()) {
    if (!value ||
        typeof value !== 'object' ||
        depth > MAX_DEPTH ||
        seen.has(value))
        return;
    seen.add(value);
    try {
        if (Array.isArray(value)) {
            for (const item of value)
                collectPrivateValues(item, knownValues, parent, depth + 1, seen);
            return;
        }
        for (const [key, child] of entries(value)) {
            const field = fieldName(key);
            if (isPrivate(field, parent) && typeof child === 'string') {
                const text = child.trim();
                if (text !== CENSOR &&
                    text.length >= 3 &&
                    text.length <= 500 &&
                    knownValues.size < 50) {
                    knownValues.add(text);
                }
            }
            else {
                collectPrivateValues(child, knownValues, field, depth + 1, seen);
            }
        }
    }
    finally {
        seen.delete(value);
    }
}
function copy(value, knownValues, field = '', parent = '', depth = 0, seen = new WeakSet()) {
    if (value === null || value === undefined)
        return value;
    if (CREDENTIALS.has(field) ||
        (parent === 'headers' &&
            /^(?:xforwardedfor|xrealip|cfconnectingip|flyclientip)$/.test(field)) ||
        /^dbqueryparameter\d+$/.test(field) ||
        /^httprequest(?:bodydata|header(?:authorization|cookie|xapikey))$/.test(field) ||
        isPrivate(field, parent))
        return CENSOR;
    if (typeof value === 'string') {
        if (idField(field))
            return redactText(value, undefined);
        if (/^(?:request|response|req|res)$/.test(parent) &&
            /^(?:data|body)$/.test(field))
            return CENSOR;
        const text = /^(?:query|sql|dbquerytext|dbstatement|dberrorquery)$/.test(field)
            ? value.replace(SQL_LITERAL, `'${CENSOR}'`)
            : value;
        return redactText(text, knownValues);
    }
    if (typeof value !== 'object' || value instanceof Date)
        return value;
    if (depth > MAX_DEPTH)
        return '[TRUNCATED: max depth]';
    if (seen.has(value))
        return '[CIRCULAR]';
    seen.add(value);
    try {
        if (Array.isArray(value)) {
            return value.map((item) => copy(item, knownValues, field, parent, depth + 1, seen));
        }
        return Object.fromEntries(entries(value).flatMap(([key, child]) => {
            const childField = fieldName(key);
            if (field === 'request' && childField === 'cookies')
                return [];
            return [
                [key, copy(child, knownValues, childField, field, depth + 1, seen)],
            ];
        }));
    }
    finally {
        seen.delete(value);
    }
}
/** Mask labelled private fields and recognizable contact data in a diagnostic copy. */
export function redactDiagnostic(value, source) {
    try {
        const knownValues = new Set();
        collectPrivateValues(value, knownValues);
        collectPrivateValues(source, knownValues);
        const pattern = [...knownValues]
            .sort((a, b) => b.length - a.length)
            .map((text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
            .join('|');
        const knownPattern = pattern
            ? new RegExp(`(?<![\\w])(?:${pattern})(?![\\w])`, 'gi')
            : undefined;
        return copy(value, knownPattern);
    }
    catch {
        // A throwing getter must not leak the source or break error reporting.
        return (typeof value === 'string' ? CENSOR : {});
    }
}
//# sourceMappingURL=diagnostic-redaction.js.map